import {
  ActionReducerMapBuilder,
  createAsyncThunk,
  EntityState,
} from '@reduxjs/toolkit';
import { classToPlain } from 'class-transformer';
import { FORM_ERROR } from 'final-form';

import { AppError } from '@vk-hr-tek/core/error';
import {
  showCustomNotification,
  showError,
} from '@vk-hr-tek/core/notifications';

import {
  CompanyEventsStat,
  CompanyGroupUsers,
  GetNewWorkflowParticipantOptionsResponse,
  SettingsService,
} from '@app/gen/settings';
import { ThunkExtra } from '@app/store';
import { Entity } from '@app/types';

import { SettingsState, SettingsWithRootState } from '../../settings.state';
import { AddParticipantDto, DeleteParticipantsDto } from '../../../dto';

import { getEventType } from './eventType.actions';

export const getParticipantOptions = createAsyncThunk<
  GetNewWorkflowParticipantOptionsResponse,
  string,
  ThunkExtra<SettingsWithRootState>
>(
  'settings/getParticipantOptions',
  async (id, { rejectWithValue, extra: { inject } }) => {
    try {
      const service = inject(SettingsService);
      const res = await service.getNewWorkflowParticipantOptions({
        params: { event_type_id: id },
      });

      return res;
    } catch (err) {
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const addParticipant = createAsyncThunk<
  void,
  {
    values: AddParticipantDto;
    actions: {
      resolve: (value: unknown) => void;
      reject: (value: unknown) => void;
    };
  },
  ThunkExtra<SettingsWithRootState>
>(
  'settings/addParticipant',
  async (
    { values, actions },
    { rejectWithValue, dispatch, getState, extra: { inject } },
  ) => {
    const { eventTypeId, companyId, groupId } = values;

    const participantsOptions =
      getState().settings.eventType.participantsOptions;
    const label =
      participantsOptions?.find((option) => option.id === groupId)?.name || '';

    try {
      const service = inject(SettingsService);
      await service.addWorkflowParticipant({
        params: { event_type_id: eventTypeId },
        body: { group_id: groupId },
      });
      dispatch(
        showCustomNotification([
          {
            title: 'Участник успешно добавлен',
            subtitle: label,
            message: 'Наблюдатель',
            type: 'info',
          },
        ]),
      );
      dispatch(getEventType({ eventTypeId, companyId }));
      actions.resolve(null);
    } catch (err) {
      actions.reject({ [FORM_ERROR]: err });
      dispatch(showError('Не удалось добавить участника'));
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const deleteParticipant = createAsyncThunk<
  void,
  {
    values: DeleteParticipantsDto;
    actions: {
      resolve: (value: unknown) => void;
      reject: (value: unknown) => void;
    };
  },
  ThunkExtra<SettingsWithRootState>
>(
  'settings/deleteParticipant',
  async (
    { values, actions },
    { rejectWithValue, dispatch, extra: { inject } },
  ) => {
    const { eventTypeId, companyId, role, group } = values;

    try {
      const service = inject(SettingsService);
      await service.deleteWorkflowParticipant({
        params: { event_type_id: eventTypeId },
        body: { participant_ids: values.participantIds },
      });
      dispatch(
        showCustomNotification([
          {
            title: 'Участник успешно удалён',
            subtitle: group === role ? '' : group,
            message: role || '',
            type: 'info',
          },
        ]),
      );
      dispatch(getEventType({ eventTypeId, companyId }));
      actions.resolve(null);
    } catch (err) {
      actions.reject({ [FORM_ERROR]: err });
      dispatch(showError('Не удалось удалить участника'));
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const settingsEventTypeParticipantsReducers = (
  builder: ActionReducerMapBuilder<
    EntityState<Entity<CompanyEventsStat | CompanyGroupUsers>, string> &
      SettingsState
  >,
) => {
  builder.addCase(getParticipantOptions.pending, (state) => {
    state.eventType.participantsOptions = null;
    state.eventType.optionsError = null;
  });
  builder.addCase(getParticipantOptions.fulfilled, (state, { payload }) => {
    state.eventType.participantsOptions = payload.groups;
    state.eventType.participantsOptionsError = null;
  });
  builder.addCase(
    getParticipantOptions.rejected,
    (state, { payload, error }) => {
      state.eventType.participantsOptions = null;
      state.eventType.participantsOptionsError =
        payload ||
        ({
          info: (error && error.message) || 'Unknown error',
          status: 500,
          source: 'client',
          title: 'Internal client error',
        } as AppError);
      state.eventType.status = 'failed';
    },
  );
  builder.addCase(addParticipant.fulfilled, (state) => {
    state.eventType.isParticipantsExpanded = true;
  });
  builder.addCase(deleteParticipant.fulfilled, (state) => {
    state.eventType.isParticipantsExpanded = true;
  });
};
